/**
 * React Context for Events Components Configuration
 * Provides abstractions for routing, images, and UI components
 */

'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { EventsComponentsConfig } from './types';

const EventsComponentsContext = createContext<EventsComponentsConfig | undefined>(undefined);

export interface EventsComponentsProviderProps {
  config: EventsComponentsConfig;
  children: ReactNode;
}

/**
 * Provider component that supplies configuration to all child components
 *
 * @example
 * ```tsx
 * import { EventsComponentsProvider } from '@uicwebmaster/events-components/config';
 * import Link from 'next/link';
 * import Image from 'next/image';
 *
 * <EventsComponentsProvider
 *   config={{
 *     LinkComponent: Link,
 *     ImageComponent: Image,
 *     baseUrl: '/events',
 *   }}
 * >
 *   <EventsList events={events} />
 * </EventsComponentsProvider>
 * ```
 */
export function EventsComponentsProvider({ config, children }: EventsComponentsProviderProps) {
  return (
    <EventsComponentsContext.Provider value={config}>{children}</EventsComponentsContext.Provider>
  );
}

/**
 * Hook to access the events components configuration
 * Returns default implementations if not provided
 * Note: getThemeColor, searchQuery, and editButton remain optional
 */
export function useEventsComponents(): Required<
  Omit<EventsComponentsConfig, 'getThemeColor' | 'searchQuery' | 'editButton'>
> &
  Pick<EventsComponentsConfig, 'getThemeColor' | 'searchQuery' | 'editButton'> {
  const config = useContext(EventsComponentsContext);

  // Default Link component (plain <a> tag)
  const DefaultLink = ({ href, className, children, target, rel, onClick, prefetch }: any) => (
    <a href={href} className={className} target={target} rel={rel} onClick={onClick}>
      {children}
    </a>
  );

  // Default Image component (plain <img> tag)
  const DefaultImage = ({
    src,
    alt,
    className,
    style,
    width,
    height,
    sizes,
    loading,
    decoding,
  }: any) => (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      width={width}
      height={height}
      sizes={sizes}
      loading={loading}
      decoding={decoding}
    />
  );

  // Default Button component
  const DefaultButton = ({ className, children, onClick, disabled, type = 'button' }: any) => (
    <button type={type} className={className} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );

  // Default Switch component (checkbox)
  const DefaultSwitch = ({ checked, onCheckedChange, id, disabled }: any) => (
    <input
      type="checkbox"
      id={id}
      checked={checked}
      onChange={e => onCheckedChange(e.target.checked)}
      disabled={disabled}
    />
  );

  // Default Pagination component
  const DefaultPagination = ({ currentPage, totalPages, onPageChange }: any) => (
    <div>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
        <button key={page} onClick={() => onPageChange(page)} disabled={page === currentPage}>
          {page}
        </button>
      ))}
    </div>
  );

  // Default FilterSearch component
  const DefaultFilterSearch = ({ query, onQueryChange, placeholder }: any) => (
    <input
      type="text"
      value={query}
      onChange={e => onQueryChange(e.target.value)}
      placeholder={placeholder}
    />
  );

  // Default FilterSummary component
  const DefaultFilterSummary = ({ totalResults, onClearAll }: any) => (
    <div>
      <span>{totalResults} results</span>
      <button onClick={onClearAll}>Clear all</button>
    </div>
  );

  // Default FilterThemes component
  const DefaultFilterThemes = ({ themes, selectedThemes, onToggleTheme }: any) => (
    <div>
      {themes.map((theme: any) => (
        <label key={theme.id}>
          <input
            type="checkbox"
            checked={selectedThemes.includes(theme.id)}
            onChange={() => onToggleTheme(theme.id)}
          />
          {theme.name}
        </label>
      ))}
    </div>
  );

  // Default colour utility
  const defaultDarkenHexColorWithOpacity = (
    color: string,
    opacity: number,
    alpha: number
  ): string => {
    // Simple fallback - just return the colour with opacity
    return color;
  };

  return {
    LinkComponent: config?.LinkComponent || DefaultLink,
    ImageComponent: config?.ImageComponent || DefaultImage,
    onNavigate: config?.onNavigate || (() => {}),
    Button: config?.Button || DefaultButton,
    Switch: config?.Switch || DefaultSwitch,
    Pagination: config?.Pagination || DefaultPagination,
    FilterSearch: config?.FilterSearch || DefaultFilterSearch,
    FilterSummary: config?.FilterSummary || DefaultFilterSummary,
    FilterThemes: config?.FilterThemes || DefaultFilterThemes,
    baseUrl: config?.baseUrl || '/events',
    darkenHexColorWithOpacity:
      config?.darkenHexColorWithOpacity || defaultDarkenHexColorWithOpacity,
    getThemeColor: config?.getThemeColor,
    searchQuery: config?.searchQuery || '',
    editButton: config?.editButton,
  };
}
